import { queryOptions, useQuery } from "@tanstack/react-query";
import { appSettingsService } from "@/shared/services/appSettings.service";
import type { AppSettings, AppSettingsResponse } from "@/shared/types/appSettings.types";

const processAppSettings = (data: AppSettingsResponse): AppSettings => ({ hiddenFeatures: data.hidden_features });

// `/app-settings` (hidden_features) est une config globale, identique sur
// toutes les pages. Clé stable + staleTime long => un seul appel réseau par
// session au lieu d'un refetch à chaque navigation/montage. Options partagées
// avec UnifiedCartContext pour que les deux consommateurs se partagent le cache.
export const APP_SETTINGS_QUERY_KEY = ["app-settings"] as const;

export const appSettingsQueryOptions = () =>
  queryOptions({
    queryKey: APP_SETTINGS_QUERY_KEY,
    queryFn: () => appSettingsService.getAppSettings(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

export const useAppSettings = () =>
  useQuery({
    ...appSettingsQueryOptions(),
    select: processAppSettings,
  });

type FeatureVisibility = {
  isHidden: boolean
  // Gates must wait on this: until app settings arrive, a hidden feature reads as visible.
  // `isPending` rather than `isLoading`, which turns false while the query is paused offline.
  isPending: boolean
};

export const useFeatureVisibility = (featureName: string): FeatureVisibility => {
  const { data, isPending } = useAppSettings();
  return { isHidden: data?.hiddenFeatures.includes(featureName) ?? false, isPending };
};

export const useIsFeatureHidden = (featureName: string): boolean => useFeatureVisibility(featureName).isHidden;
